import React, { useState } from 'react';

import { Alert, Stack } from '@mui/material';

import PromoAiWorkflowLauncher, { type AiWorkflowAction } from '~/components/Promo/PromoAiWorkflowLauncher';
import PromoFeature from '~/components/Promo/PromoFeature';
import PromoVoucherAiDialog from '~/components/Promo/PromoVoucherAiDialog';

import { useResolvePromoAiWorkflow } from '~/hooks/apis/promo/useResolvePromoAiWorkflow';
import { useResolveVoucherAiWorkflow } from '~/hooks/apis/promo/useResolveVoucherAiWorkflow';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';

import { type AiCodeKind, type AiCodeOperation } from '~/services/PromoVoucherAiService';

type Props = { kind: AiCodeKind; initialAction?: AiWorkflowAction };

export default function PromoAiWorkflowEntry({ kind, initialAction }: Props) {
  const { push } = useAppNavigation();
  const { promoBrand } = useCurrentPromoBrand();
  const promoResolve = useResolvePromoAiWorkflow();
  const voucherResolve = useResolveVoucherAiWorkflow();
  const resolve = kind === 'promo' ? promoResolve : voucherResolve;
  const [error, setError] = useState('');
  const [savedCodeName, setSavedCodeName] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ name: string; operation: AiCodeOperation; query: string } | null>(null);

  const handleContinue = async (name: string, action: AiWorkflowAction, query: string) => {
    setError('');
    setSavedCodeName(null);
    try {
      const existing = action === 'create' ? null : await resolve.mutateAsync({ name, brand: promoBrand });
      if (action === 'update' && !existing) {
        throw new Error(`No ${kind} matched this name for the selected brand.`);
      }
      const operation = existing ? 'update' : 'create';
      if (kind === 'voucher') {
        const params = new URLSearchParams({ aiWorkflow: action, aiVoucherName: name });
        const path = existing ? `/marketing/vouchers/${encodeURIComponent(existing.id)}` : '/marketing/vouchers/new';
        push(`${path}?${params}`, { aiRequest: query });
      } else {
        setDialog({ name, operation, query });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to resolve the AI workflow.');
    }
  };

  return (
    <Stack spacing={2}>
      <PromoAiWorkflowLauncher
        kind={kind}
        initialAction={initialAction}
        isPending={resolve.isPending}
        onContinue={handleContinue}
      />
      {error && <Alert severity="error">{error}</Alert>}
      {dialog && (
        <PromoFeature featureKey="ai-promo-tools-access" presentation="raw" accessMode="show-warning">
          <PromoVoucherAiDialog
            open
            kind="promo"
            initialCodeName={dialog.name}
            initialOperation={dialog.operation}
            initialRequest={dialog.query}
            onClose={() => {
              setDialog(null);
              if (savedCodeName) {
                push(`/promos/code/${encodeURIComponent(savedCodeName)}`);
              }
            }}
            onSaved={setSavedCodeName}
          />
        </PromoFeature>
      )}
    </Stack>
  );
}
